import {
    EliminationDetailReportResponse,
    EliminationExceptionJournalEntry,
    EliminationExceptionsReportResponse,
} from "@common/schemas/domains/accounting/elimination.schema";
import type {
    IntercompanyTransactionRow,
    IntercompanyTransactionSource,
} from "./intercompanyTransactions.types";

type DetailRow = EliminationDetailReportResponse["rows"][number];
type ExceptionRow = EliminationExceptionsReportResponse["rows"][number];

export function getDetailSourceEntryKey({
    detailRow,
    source,
    transactionId,
    journalEntryId,
}: {
    detailRow: DetailRow;
    source: IntercompanyTransactionSource;
    transactionId: number | undefined;
    journalEntryId: string | undefined;
}): string {
    if (source === "transaction") {
        return `transaction:${detailRow.pair.entityId}:${transactionId}`;
    }
    const [journalEntry] = detailRow.journalEntries;
    return journalEntry
        ? [
              source,
              detailRow.pair.entityId,
              journalEntry.accountingLedgerId,
              journalEntry.referenceNumber,
          ].join(":")
        : `${source}:${detailRow.pair.entityId}:${journalEntryId}`;
}

export function getExceptionSourceEntryKey({
    exceptionRow,
    side,
    source,
    sourceTransactionId,
    journalEntry,
}: {
    exceptionRow: ExceptionRow;
    side: ExceptionRow["sides"][number];
    source: IntercompanyTransactionSource;
    sourceTransactionId: number | undefined;
    journalEntry: EliminationExceptionJournalEntry | null;
}): string {
    if (sourceTransactionId !== undefined) {
        return `transaction:${side.entityId}:${sourceTransactionId}`;
    }
    if (journalEntry === null) {
        if (source === "transaction") {
            return `transaction-ambiguous:${side.entityId}:${exceptionRow.id}`;
        }
        return `${source}:${side.entityId}:${exceptionRow.id}`;
    }
    return [
        source,
        side.entityId,
        journalEntry.accountingLedgerId,
        journalEntry.referenceNumber,
    ].join(":");
}

export function getDetailOccurrenceKey(detailRow: DetailRow): string {
    const [journalEntry] = detailRow.journalEntries;
    if (detailRow.journalEntries.length === 1 && journalEntry) {
        return getJournalLineOccurrenceKey({
            factType: detailRow.factType,
            entityId: detailRow.pair.entityId,
            otherEntityId: detailRow.pair.otherEntityId,
            accountId: detailRow.accountId,
            journalEntry,
        });
    }
    return `detail:${detailRow.id}`;
}

export function getExceptionOccurrenceKey({
    exceptionRow,
    side,
    sideIndex,
    journalEntry,
}: {
    exceptionRow: ExceptionRow;
    side: ExceptionRow["sides"][number];
    sideIndex: number;
    journalEntry: EliminationExceptionJournalEntry | null;
}): string {
    if (journalEntry) {
        const otherEntityId =
            side.entityId === exceptionRow.pair.entityId
                ? exceptionRow.pair.otherEntityId
                : exceptionRow.pair.entityId;
        return getJournalLineOccurrenceKey({
            factType: exceptionRow.factType,
            entityId: side.entityId,
            otherEntityId,
            accountId: side.accountId,
            journalEntry,
        });
    }
    // Without a shared journal-line id, the exception occurrence cannot be
    // safely correlated to detail or to another exception with the same amount.
    return [
        "exception",
        exceptionRow.id,
        sideIndex,
        side.entityId,
        side.accountId,
    ].join(":");
}

export function finalizeIntercompanyTransactionRow(
    row: IntercompanyTransactionRow,
): IntercompanyTransactionRow {
    const [account] = row.counterpartAccounts;
    if (row.counterpartAccounts.length === 1 && account) {
        return {
            ...row,
            accountId: account.id,
            accountCode: account.code,
            accountLabel: account.label,
            amount:
                row.counterpartAmount === 0
                    ? row.intercompanyAmount
                    : row.counterpartAmount,
        };
    }
    if (row.counterpartAccounts.length > 1) {
        return {
            ...row,
            accountId: "",
            accountCode: null,
            accountLabel: "Split",
            amount:
                row.counterpartAmount === 0
                    ? row.intercompanyAmount
                    : row.counterpartAmount,
        };
    }
    return {
        ...row,
        accountId: "",
        accountCode: null,
        accountLabel: "",
        amount: row.intercompanyAmount,
    };
}

function getJournalLineOccurrenceKey({
    factType,
    entityId,
    otherEntityId,
    accountId,
    journalEntry,
}: {
    factType: DetailRow["factType"];
    entityId: number;
    otherEntityId: number;
    accountId: string;
    journalEntry: Pick<
        EliminationExceptionJournalEntry,
        "accountingLedgerId" | "journalEntryId"
    >;
}): string {
    return [
        "journal-line",
        factType,
        entityId,
        otherEntityId,
        accountId,
        journalEntry.accountingLedgerId,
        journalEntry.journalEntryId,
    ].join(":");
}
